/**
 * Permission rules for the settings sections, in one place. Each rule is the
 * same test the old tab builders (accountTabs / locationTabs) and the
 * Financials sub-tab row applied; the registry parity test proves it.
 */
import type { SettingsAccess } from './types';

export const has = (a: SettingsAccess, permission: string): boolean => a.permissions.includes(permission);
const any = (a: SettingsAccess, ...permissions: string[]): boolean => permissions.some((p) => has(a, p));

/** Any granular policy writer, the legacy coarse writers, or policies:read (read-only). */
export const canSeeAccountPolicySurfaces = (a: SettingsAccess): boolean =>
  a.isSystemAdmin ||
  any(
    a,
    'policies:read',
    'policies:manage',
    'policies:workorders:manage',
    'policies:worksessions:manage',
    'policies:accounting:manage',
    'accounts:update'
  );

/** The Customers module's switch: whoever may edit the account's details. */
export const canOpenCustomers = (a: SettingsAccess): boolean => a.isSystemAdmin || any(a, 'accounts:update', 'account:admin');

export const canOpenPdr = (a: SettingsAccess): boolean => a.isSystemAdmin || any(a, 'pdr:manage', 'pdr:estimate');
export const canOpenAccountFeeds = (a: SettingsAccess): boolean => a.isSystemAdmin || has(a, 'data_feeds:manage');
export const canOpenPartners = (a: SettingsAccess): boolean => a.isSystemAdmin || has(a, 'partner_connections:manage');
export const canOpenReporting = (a: SettingsAccess): boolean => !!a.canViewAccountReports;

/** Account money surfaces (formerly the Financials sub-tabs). */
export const canWriteAccountDefaults = (a: SettingsAccess): boolean => a.isSystemAdmin || any(a, 'accounts:update', 'account:admin');
export const canWriteAccountPricing = (a: SettingsAccess): boolean =>
  a.isSystemAdmin || any(a, 'policies:manage', 'policies:pricing:manage');
export const canOpenAccounting = (a: SettingsAccess): boolean => a.isSystemAdmin || has(a, 'integrations:manage');
export const canOpenBill = (a: SettingsAccess): boolean => a.isSystemAdmin || has(a, 'billing:report:read');

/** Location rules (locationTabs). */
export const canSeeLocationPolicySurfaces = (a: SettingsAccess): boolean =>
  a.isSystemAdmin ||
  any(a, 'locations:update', 'policies:read', 'policies:manage', 'policies:workorders:manage', 'policies:worksessions:manage');
const canEditLocation = (a: SettingsAccess): boolean => a.isSystemAdmin || has(a, 'locations:update');
export const canOpenLocationMoney = (a: SettingsAccess): boolean => canEditLocation(a) || has(a, 'policies:pricing:manage');
export const canOpenLocationFeeds = (a: SettingsAccess): boolean => canEditLocation(a) || has(a, 'data_feeds:manage');
// A store's Invoicing: canOpenStoreInvoicing (modals/invoicingSections), its
// Setup for rule editors and its result lists for whoever may see them.
